import { api } from './Api';
import { snakeToCamel } from '../utils/CaseUtils';

export const directoryDefaults = {
  businessId: 'unselected', clockedIn: 'all', lastClockInDays: '7', neverClockedIn: '',
  sort: 'last_clock_in_desc', page: 1, pageSize: 50,
};

export { canViewDirectories as canViewDriverDirectory } from './DirectoryAccess';

export const getDirectoryBusinessOptions = async () => {
  const response = await api.get('/deliveryops/businesses/options', undefined, { cache: 'no-store' });
  const ids = new Set();
  if (!Array.isArray(response) || response.some((option) => {
    if (!option || !Number.isSafeInteger(option.id) || option.id <= 0 ||
        typeof option.name !== 'string' || ids.has(option.id)) return true;
    ids.add(option.id);
    return false;
  })) throw new Error('Invalid authorized business options response.');
  return response.map(({ id, name }) => ({ id, name }));
};

export const businessOptionsErrorMessage = (error) => error?.status === 403
  ? 'Access denied to authorized businesses. Sign in with an authorized dispatcher account or contact an administrator.'
  : 'Unable to load authorized businesses. Check your connection and retry.';

const positiveInteger = (value) =>
  /^[1-9]\d*$/.test(String(value)) && Number.isSafeInteger(Number(value));

export const validateDirectoryParams = (input) => {
  const p = { ...directoryDefaults, ...input };
  if (p.businessId !== '' && !positiveInteger(p.businessId)) {
    throw new Error('Enter a positive Business ID without spaces or leading zeros.');
  }
  if (!['all', 'true', 'false'].includes(p.clockedIn) ||
      !['', 'true', 'false'].includes(p.neverClockedIn) ||
      !['', '7', '30', '90'].includes(String(p.lastClockInDays)) ||
      !['last_clock_in_desc', 'last_clock_in_asc', 'name_asc'].includes(p.sort)) {
    throw new Error('Choose valid directory filters and sorting.');
  }
  if (p.lastClockInDays !== '' && (p.businessId === '' || p.neverClockedIn === 'true')) {
    throw new Error('Recent activity requires a Business ID and cannot include never-recorded history.');
  }
  if (!positiveInteger(p.page) || !positiveInteger(p.pageSize) || Number(p.pageSize) > 100) {
    throw new Error('Page must be positive and page size must be between 1 and 100.');
  }
  return p;
};

export const directoryQueryParams = (input) => {
  const p = validateDirectoryParams(input);
  return {
    ...(p.businessId !== '' ? { business_id: p.businessId } : {}),
    clocked_in: p.clockedIn,
    ...(p.lastClockInDays !== '' ? { last_clock_in_days: p.lastClockInDays } : {}),
    ...(p.neverClockedIn !== '' ? { never_clocked_in: p.neverClockedIn } : {}),
    sort: p.sort, page: p.page, page_size: p.pageSize,
  };
};

const validTime = (value) =>
  typeof value === 'string' && /Z$/.test(value) && Number.isFinite(Date.parse(value));
const nullableString = (value) => value === null || typeof value === 'string';
const count = (value) => Number.isSafeInteger(value) && value >= 0;
const invalidResponse = () => { throw new Error('Invalid directory response. Refresh or contact support.'); };

export const mapDriverDirectory = (response, params) => {
  const context = response?.filter_context;
  if (!response || !Array.isArray(response.results) || !context ||
      !count(response.total_results) || !count(response.total_pages) ||
      response.page !== Number(params.page) || response.page_size !== Number(params.page_size) ||
      response.total_pages !== Math.ceil(response.total_results / response.page_size) ||
      !validTime(response.as_of_utc) ||
      context.business_id !== (params.business_id ? Number(params.business_id) : null) ||
      context.last_clock_in_days !== (params.last_clock_in_days ? Number(params.last_clock_in_days) : null) ||
      !nullableString(context.business_timezone)) invalidResponse();
  if (params.last_clock_in_days) {
    if (!context.business_timezone || !validTime(context.from_utc) || !validTime(context.to_utc) ||
        Date.parse(context.from_utc) >= Date.parse(context.to_utc)) invalidResponse();
    try { new Intl.DateTimeFormat('en', { timeZone: context.business_timezone }); }
    catch { invalidResponse(); }
  } else if (context.from_utc !== null || context.to_utc !== null) invalidResponse();
  if (!params.business_id && context.business_timezone !== null) invalidResponse();
  const expected = Math.max(0, Math.min(response.page_size,
    response.total_results - (response.page - 1) * response.page_size));
  if (response.results.length !== expected) invalidResponse();
  const ids = new Set();
  response.results.forEach((row) => {
    if (!Number.isSafeInteger(row.id) || row.id <= 0 || ids.has(row.id) ||
        row.is_active !== true || typeof row.is_clocked_in_anywhere !== 'boolean' ||
        ![row.first_name, row.last_name, row.phone].every(nullableString) ||
        !(row.last_clock_in_at_utc === null || (validTime(row.last_clock_in_at_utc) &&
          Date.parse(row.last_clock_in_at_utc) <= Date.parse(response.as_of_utc))) ||
        !Array.isArray(row.business_memberships) || !row.business_memberships.length) invalidResponse();
    ids.add(row.id);
    row.business_memberships.forEach((membership) => {
      if (!Number.isSafeInteger(membership.business_id) || membership.business_id <= 0 ||
          !nullableString(membership.business_name) || !nullableString(membership.timezone)) invalidResponse();
    });
    if (row.home_address !== null &&
        (!row.home_address || !['address1', 'address2', 'city', 'state', 'postal_code', 'country_code']
          .every((key) => nullableString(row.home_address[key])))) invalidResponse();
  });
  return snakeToCamel(response);
};

export const getDriverDirectory = async (input) => {
  const params = directoryQueryParams(input);
  return mapDriverDirectory(await api.get('/deliveryops/drivers/directory', params, {
    cache: 'no-store',
  }), params);
};

export const directoryErrorMessage = (error) => {
  if (error?.status === 422 || /BUSINESS_TIMEZONE_UNAVAILABLE/.test(error?.message || '')) {
    return 'Business timezone unavailable. Ask an administrator to configure its timezone, or remove the recent-activity filter.';
  }
  if (error?.status === 403) return 'Access denied. Use an authorized Business ID or sign in with a dispatcher account.';
  if (error?.status === 400) return 'Invalid directory filters. Check the Business ID and filter combination, then try again.';
  return 'Unable to load the driver directory. Check your connection and retry.';
};

export const formatDirectoryTime = (timestamp, timezone) => {
  if (!timestamp || !validTime(timestamp)) return 'Time unavailable';
  if (!timezone) return 'Timezone unavailable';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone, year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(new Date(timestamp));
  } catch { return 'Timezone unavailable'; }
};